import {
  HttpErrorResponse,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  STAFF_HTTP_API_BASE_URL,
  normalizeBaseUrl,
} from '@sbe-spinoff-client/staff-http';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  isOidcStaffAuthRoute,
  isStaffTokenlessAuthRoute,
} from './staff-oidc-auth-url.util';
import { StaffOidcAuthService } from './staff-oidc-auth.service';

/**
 * API base URL 로 나가는 요청에 access token(`Authorization: Bearer`)을 붙이고, 401 이면
 * refresh 를 1회 시도해 재전송한다. 이전 `staffRedirectOn401Interceptor` 를 대체한다.
 *
 *  - refresh·signout·authorize-redirect(`isStaffTokenlessAuthRoute`)는 토큰도 재시도도 없이
 *    통과한다(refresh 의 401 이 다시 refresh 를 부르는 루프 방지).
 *  - 동시 401 은 `StaffOidcAuthService.refreshAccessToken()` 이 refresh 하나로 합친다.
 *  - refresh 실패 또는 재전송도 401 이면 `redirectToSignIn()`. 단 `/staff/auth/*` 호출(`me`)은
 *    `staffAuthGuard` 가 `loadMe()` 실패를 스스로 감지해 이동하므로 여기서 이동하지 않고 그대로
 *    throw 한다(guard 쪽 redirect 와 경쟁 방지). 앱이 `handlesUnauthorized` 로 지정한 요청도 이동하지 않는다.
 *
 * 등록 순서: `staffApiBaseUrlInterceptor` → `staffCredentialsInterceptor` → 이 인터셉터
 * (절대 URL 기준으로 판별하므로 base URL 인터셉터가 먼저여야 한다). API base URL 대상이 아닌
 * 요청(제3자 절대 URL 등)에는 토큰을 싣지 않는다.
 */
export const staffBearerInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(StaffOidcAuthService);
  const base = normalizeBaseUrl(inject(STAFF_HTTP_API_BASE_URL));

  const isApiRequest = base ? req.url.startsWith(base) : req.url.startsWith('/');
  if (!isApiRequest || isStaffTokenlessAuthRoute(req.url)) return next(req);

  const withToken = (r: HttpRequest<unknown>) => {
    const token = auth.accessToken();
    return token
      ? r.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : r;
  };
  const failUnauthorized = (err: unknown) => {
    if (!isOidcStaffAuthRoute(req.url) && !auth.handlesUnauthorized(req.url)) auth.redirectToSignIn();
    return throwError(() => err);
  };

  return next(withToken(req)).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      return from(auth.refreshAccessToken()).pipe(
        switchMap((ok) => {
          if (!ok) return failUnauthorized(err);
          // 재전송은 1회뿐 — 다시 401 이면 이동한다.
          return next(withToken(req)).pipe(
            catchError((retryErr: unknown) =>
              retryErr instanceof HttpErrorResponse && retryErr.status === 401
                ? failUnauthorized(retryErr)
                : throwError(() => retryErr),
            ),
          );
        }),
      );
    }),
  );
};
